import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { TagTemplateElement, TagTemplateElementDTO } from 'src/app/_model/tag-template-element';
import { ClientStateService } from 'src/app/_services/client-state/logic/client-state.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { TagStatesService } from 'src/app/_services/tag-states/logic/tag-states.service';
import { TagTemplatesElementsDaoService } from '../../dao/tag-templates-elements-dao.service';
import { TagTemplatesElementsService } from '../tag-templates-elements.service';

@Injectable()
export class TagTemplatesElementsLogicService implements TagTemplatesElementsService {
  initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);
  private currentSjobId: IMSId;
  private tags: TagTemplateElement[] = [];

  constructor(private logger: LoggingService,
    private tagTemplatesElementsDaoService: TagTemplatesElementsDaoService,
    private clientStateService: ClientStateService,
    private servicejobsService: ServicejobsService,
    private tagStatesService: TagStatesService) {
    this.initialize();

    this.clientStateService.theState.subscribe(async state => {
      if (!IMSObject.equals(this.currentSjobId, state?.serviceJobId)) {
        this.currentSjobId = state.serviceJobId;
        this.loadTagStatesForPhotodocForCurrentServiceJob();
      }
    });

    this.servicejobsService.servicejobs$.subscribe(sJobs => {
      this.loadTagStatesForPhotodocForCurrentServiceJob();
    });

    this.tagStatesService.tagStatesForPhotodocForServiceJobs$.subscribe((tagStates) => {
      this.loadTagStatesForPhotodocForCurrentServiceJob();
    });
  }

  async updateTagTemplatesElements(tagTemplatesElementsDto: TagTemplateElementDTO[]): Promise<void> {
    this.logger.log(this.constructor.name, '- Received new tagtemplateselements from other IMS component', tagTemplatesElementsDto);

    try {
      this.tags = [];
      tagTemplatesElementsDto?.forEach(tagTemplateDto => {
        this.tags.push(TagTemplateElement.fromDto(tagTemplateDto));
      });

      await this.tagTemplatesElementsDaoService.saveTagTemplatesElements(this.tags);
    } catch (error) {
      this.logger.error(this.constructor.name, 'Error parsing TagTemplatesElements: ', error?.message);
      throw error;
    }
  }

  async reset(): Promise<void> {
    this.tagTemplatesElementsDaoService.reset();
  }

  async clear(): Promise<void> {
    await this.tagTemplatesElementsDaoService.clear();
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalTagTemplateElements();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  private async loadLocalTagTemplateElements(): Promise<void> {
    try {
      this.tags = await this.tagTemplatesElementsDaoService.getTagTemplatesElements();
      this.logger.log(
        this.constructor.name,
        '- Loaded local TagTemplateElements',
        this.tags
      );
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local TagTemplateElements: ',
        error.message
      );
      throw error;
    }
  }

  private async loadTagStatesForPhotodocForCurrentServiceJob(): Promise<void> {
    if (this.currentSjobId == null) {
      return;
    }

    const serviceJob = this.servicejobsService.getServiceJobById(this.currentSjobId);
    this.tagTemplatesElementsDaoService.getTagStatesForPhotodocForCurrentServiceJob(serviceJob, this.tags);
  }

  getTagHierarchyForTagId(tagId: number): TagTemplateElement[] {
    const tagHierarchy: TagTemplateElement[] = [];
    let currentTag = this.tags.find(tag => tag.getImsId() === tagId);
    do {
      tagHierarchy.unshift(currentTag);
      currentTag = this.tags.find(tag => tag.getImsId() === currentTag.parent?.getImsId());
    } while (currentTag);

    return tagHierarchy;
  }

  public getTagTemplateElementById(id: IMSId): TagTemplateElement {
    if (id) {
      return this.tags.find((tag) => {
        return tag.id.imsId === id.imsId && tag.id.secondaryId === id.secondaryId;
      });
    } else {
      return undefined;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
